import { CalculationLineApi, CalculationTailApi } from "src/app/types";
import { Aggregation, CalculationSelection, CalculationState, ViewModelLineType, ViewModelLine, WarningType, calculateCostsForCalculationLine, createEmptyAggregation, mergeAggregatedValues, Language, Translation } from "../calculation-state";

export type VatType = 'NL-NONE' | 'NL-LOW' | 'NL-HIGH';
export type VatRate = {
    label: Translation;
    percentage: number;
    default: boolean;

}

export const VatRates: Record<VatType, VatRate> = {
    'NL-NONE': { label: { nl: 'Geen BTW', en: 'No VAT' }, percentage: 0, default: false },
    'NL-LOW': { label: { nl: 'B.T.W. laag tarief, 9% over', en: 'Low rate' }, percentage: 0.09, default: false },
    'NL-HIGH': { label: { nl: 'B.T.W. hoog tarief, 21% over', en: 'High rate' }, percentage: 0.21, default: true },
}

export const LOW_VAT_CATEGORIES = ['40.', '46.']

export const LINE_CODE_SORT_FNC = (l1: CalculationLineApi, l2: CalculationLineApi) => l1.code === l2.code ? LINE_SORTORDER_FNC(l1, l2) : l1.code < l2.code ? -1 : 1;
export const LINE_SORTORDER_FNC = (l1: CalculationTailApi, l2: CalculationTailApi) => (l1.sortOrder ?? 0) - (l2.sortOrder ?? 0);
/**
 * Convert backend calculation lines to view model lines
 * @param state current calculation state
 * @returns view model lines, which are copies of the backend lines enriched with view model information
 */
export const createViewModel = (
    state: CalculationState,
    selectLineId?: string,
    renderLanguage?: 'nl' | 'en'
): CalculationState => {
    // console.log('LANG', renderLanguage);
    // DEBUG:
    const t0 = new Date().valueOf();

    // To build the graph, sort the lines
    // Assumption that code is fixed length for all lines
    // It's format is XX.XX where X is a letter or number
    const sortedLines = [...state.lines].sort(LINE_CODE_SORT_FNC);

    // Keep track of all lines with net provisional costs
    // These costs are excluded from tail calculation
    const netProvisionalCosts: ViewModelLine[] = [];

    let currentCat = '';
    let currentSub = '';
    let index = 0;
    let offset = 0;

    // Keep track of summed quantities and amounts on three levels
    // - Calculation, the grand total
    // - Category subtotal
    // - Subcategory subtotal
    const calcAggregationExclVat: Aggregation = createEmptyAggregation();
    let calcAggregationInclVat: Aggregation = createEmptyAggregation();
    let catAggregation: Aggregation = createEmptyAggregation();
    let subAggregation: Aggregation = createEmptyAggregation();
    let sectionAggregation: Aggregation = createEmptyAggregation();

    // Keep track of VAT
    // Only need totals per VAT type
    const vatOverAmount: Record<VatType, Aggregation> = {
        'NL-NONE': createEmptyAggregation(),
        'NL-LOW': createEmptyAggregation(),
        'NL-HIGH': createEmptyAggregation(),
    }

    const warnings: WarningType[] = [];

    const result: ViewModelLine[] = sortedLines.flatMap((line, i) => {

        // Also, the calculation lines itself will have some calculated values
        // These are also stored in an Aggregation object

        // Create view model lines for calculation line
        const viewModelLines: ViewModelLine[] = [];

        const [lineCat, lineSub] = line.code.split('.');

        if (!lineCat || !lineSub) {
            throw new Error('Invalid code');
        }

        // Check if new category starts
        if (currentCat !== lineCat) {
            // Category header line contains sum of all line values
            // But it is displayed above the category
            // So aggregation object is added to line and filled with subsequent calculation line values
            catAggregation = createEmptyAggregation();

            viewModelLines.push({
                type: ViewModelLineType.CATEGORY,
                index: index++,
                aggregation: catAggregation,
                description: state.templateDict[lineCat] ?? '',
                code: lineCat,
                section: 'calculation'
            });

            currentCat = lineCat;

            // Always start with new sub if cat changed
            currentSub = undefined;
        }

        // Check if new subcategory starts
        if (currentSub !== lineSub) {

            subAggregation = createEmptyAggregation();

            viewModelLines.push({
                type: ViewModelLineType.SUBCATEGORY,
                index: index++,
                aggregation: subAggregation,
                description: state.templateDict[line.code] ?? '',
                code: line.code,
                section: 'calculation'
            });

            sectionAggregation = createEmptyAggregation();

            currentSub = lineSub;
        }

        let lineAggregation: Aggregation | undefined = undefined;

        if (!line.isTextLine) {
            // Horrible hack, but we do not want default percentages if the line has unit sptN or spt
            // These are net and gross provisional costs respectively
            // Since these are estimates, replaced by actual costs, default percentages are not valid
            // TODO: Provisional costs should not be a unit of measurement, but just a flag or label
            // Until then, we will ignore the default percentages if the unit is sptN or spt
            const isProvisionalCosts = line.unit === 'sptN' || line.unit === 'spt';
            const useThesePercentages = isProvisionalCosts ? undefined : state.meta?.percentages;

            // Get calculated values for this line
            lineAggregation = calculateCostsForCalculationLine(line, state.hourRates, useThesePercentages);

            // Add line values to all aggregation levels
            // - Calculation
            // - Category
            // - Subcategory
            mergeAggregatedValues(calcAggregationExclVat, lineAggregation);
            mergeAggregatedValues(catAggregation, lineAggregation);
            mergeAggregatedValues(sectionAggregation, lineAggregation);
            mergeAggregatedValues(subAggregation, lineAggregation);

            // If no VAT type is set, use the default VAT type
            const vatType = LOW_VAT_CATEGORIES.some(cat => line.code.startsWith(cat)) ? 'NL-LOW' : 'NL-HIGH';

            // Merge with VAT totals
            mergeAggregatedValues(vatOverAmount[vatType], lineAggregation);
        }

        const lineWarnings = getLineWarnings(line);

        warnings.push(...lineWarnings ?? []);

        // Add one view model line for the calculation line itself
        const newLine: ViewModelLine = {
            type: line.isTextLine ? ViewModelLineType.TEXT : ViewModelLineType.CALCULATION,
            index: index++,
            line: JSON.parse(JSON.stringify(line)),
            aggregation: lineAggregation,
            warnings: lineWarnings,
            section: 'calculation'
        };

        // Only take the net provisional costs, if they are not zero
        if (line.unit === 'sptN' && !!newLine.aggregation?.all.gross) {
            netProvisionalCosts.push(newLine);
        }

        viewModelLines.push(newLine);

        // If an end of section is added after this line, 
        // or it is the last line,
        // or it is the last in the subcategory,
        // a subtotal line is added after it
        if (line.endOfSection || ((i === sortedLines.length - 1 || sortedLines[i + 1].code !== line.code) && !isEmptyAggregation(sectionAggregation))) {
            viewModelLines.push({
                type: ViewModelLineType.SUBTOTAL,
                index: index++,
                offset: offset += 20,
                // line: JSON.parse(JSON.stringify(line)),
                description: renderLanguage === Language.EN ? 'Subtotal' : 'Subtotaal',
                aggregation: sectionAggregation,
                section: 'calculation'
            });

            sectionAggregation = createEmptyAggregation();
        }

        return viewModelLines;
    })

    // Add grand total line
    result.push({
        type: ViewModelLineType.TOTAL,
        aggregation: deepCopy(calcAggregationExclVat),
        description: state.tail?.length > 0 ? renderLanguage === Language.EN ? 'Subtotal calculation excl. V.A.T.' : 'Subtotaal calculatie excl. B.T.W.' : renderLanguage === Language.EN ? 'Total calculation' : 'Totaal calculatie',
        index: index++,
        section: 'tail',
    })

    let sortedTailLines: CalculationTailApi[] = [];

    const simpleGlobalAggregation = () => Object.assign(createEmptyAggregation(),
        {
            all: {
                gross: calcAggregationExclVat.all.gross,
                net: calcAggregationExclVat.all.net,
                norm: 0,
                unitPrice: 0,
                hours: 0
            }
        } satisfies Partial<Aggregation>)

    // VAT calculation til here was just calculating the VAT amounts for calculation lines
    // For the overhead and profit lines, we will use the ratio of used VAT category amounts
    // E.g. if 80% of the calculation was taxed with 21% VAT, we will use that ratio for the overhead and profit lines
    const vatRatios: Record<VatType, number> = {
        'NL-NONE': 0,
        'NL-LOW': (vatOverAmount['NL-LOW'].all.gross ?? 0) / calcAggregationExclVat.all.gross,
        'NL-HIGH': (vatOverAmount['NL-HIGH'].all.gross ?? 0) / calcAggregationExclVat.all.gross,
    }

    // console.log('VAT RATIOS', vatRatios);

    if (state.tail?.length > 0) {
        // Add tail category header
        result.push({
            type: ViewModelLineType.CATEGORY,
            index: index++,
            aggregation: createEmptyAggregation(),
            description: renderLanguage === Language.EN ? 'Overhead' : 'Staart',
            code: '',
            section: 'tail'
        })

        // Before the tail, the net provisional costs are subtracted from the running total
        // Tail costs do not apply to this category
        if (!!state.meta?.includeProvisionalPostLinesAfterTail && netProvisionalCosts.length > 0) {

            // Add subcategory header for net provisional costs
            result.push({
                type: ViewModelLineType.SUBCATEGORY,
                index: index++,
                aggregation: createEmptyAggregation(),
                description: renderLanguage === Language.EN ? 'Provisional costs' : 'Stelposten',
                code: '',
                section: 'tail'
            });

            // The net provisional costs will be aggregated to a single line
            const netProvAggr = createEmptyAggregation();

            for (const prov of netProvisionalCosts) {
                // Create aggregation with inversed costs
                const runningAggregation = Object.assign(
                    createEmptyAggregation(),
                    {
                        all: {
                            gross: -1 * prov.aggregation.all.gross,
                            net: -1 * prov.aggregation.all.net,
                            norm: 0,
                            unitPrice: 0

                        }
                    } satisfies Partial<Aggregation>
                );

                // Sum of all net provisional costs will be shown
                mergeAggregatedValues(netProvAggr, runningAggregation)
            }

            // Update calculation aggregation
            mergeAggregatedValues(calcAggregationExclVat, netProvAggr);

            // Aggragate all net provisional costs to one line
            result.push({
                type: ViewModelLineType.READ_ONLY,
                index: index++,
                description: renderLanguage === Language.EN ? 'Exclude provisional costs' : 'Minus stelposten',
                aggregation: netProvAggr,
                line: netProvisionalCosts[0].line,
                section: 'tail'
            });


            // Add grand total line
            result.push({
                type: ViewModelLineType.TOTAL,
                aggregation: simpleGlobalAggregation(),
                description: renderLanguage === Language.EN ? 'Subtotal without net provisional costs' : 'Subtotaal calculatie zonder netto stelposten',
                index: index++,
                section: 'tail',
            })
        }

        // If there are tail lines, sort and add them as well
        sortedTailLines = [...state.tail].sort(LINE_SORTORDER_FNC);

        let lastSubTotal = createEmptyAggregation();
        lastSubTotal.all.gross = calcAggregationExclVat.all.gross;

        // Add subcategory header for net provisional costs
        result.push({
            type: ViewModelLineType.SUBCATEGORY,
            index: index++,
            aggregation: createEmptyAggregation(),
            description: renderLanguage === Language.EN ? 'Overhead costs' : 'Staartkosten',
            code: '',
            section: 'tail'
        });

        for (const tailLine of sortedTailLines) {
            const lineAggregation = createEmptyAggregation();

            switch (tailLine.type) {
                case 'SUBTOTAL':
                    // Replaced by end of section
                    throw new Error('SUBTOTAL line in tail was not expected. Replaced by endOfSection property');
                case 'AMOUNT':
                    // Add the amount to the gross value
                    lineAggregation.all.gross = tailLine.amount;
                    mergeAggregatedValues(calcAggregationExclVat, lineAggregation);
                    addOverheadAndProfitToVatTotals((tailLine.amount ?? 0), vatOverAmount, vatRatios);

                    result.push({
                        type: ViewModelLineType.TAIL_AMOUNT,
                        index: index++,
                        description: tailLine.description ?? '',
                        line: tailLine,
                        aggregation: lineAggregation,
                        section: 'tail'
                    });
                    break;
                case 'PERCENTAGE':
                    // Add the amount, calculated by multiplying the percentage with the current total, to the gross value
                    const lineAmount = (tailLine.percent ?? 0) * lastSubTotal.all.gross;
                    lineAggregation.all.gross = lineAmount;
                    mergeAggregatedValues(calcAggregationExclVat, lineAggregation);
                    addOverheadAndProfitToVatTotals(lineAmount, vatOverAmount, vatRatios);

                    result.push({
                        type: ViewModelLineType.TAIL_PERCENTAGE,
                        index: index++,
                        description: tailLine.description ?? '',
                        line: tailLine,
                        aggregation: lineAggregation,
                        section: 'tail'
                    });
                    break;

                default:
                    result.push({
                        type: ViewModelLineType.TAIL_TEXT,
                        index: index++,
                        description: tailLine.description ?? '',
                        line: tailLine,
                        aggregation: lineAggregation,
                        section: 'tail'
                    });
                    break;

            }

            // Add subtotal line if end of section is true
            if (tailLine.endOfSection) {
                // console.log('END OF SECTION', tailLine);

                // Start new subtotal for percentage calculation
                lastSubTotal = createEmptyAggregation();
                lastSubTotal.all.gross = calcAggregationExclVat.all.gross;

                // Add subtotal line
                result.push({
                    type: ViewModelLineType.TAIL_SUBTOTAL,
                    index: index++,
                    description: renderLanguage === Language.EN ? 'Subtotal' : 'Subtotaal',
                    line: tailLine,
                    aggregation: lastSubTotal,
                    section: 'tail'
                });
            }
        }

        // Before the tail, the net provisional costs are subtracted from the running total
        // Tail costs do not apply to this category
        if (!!state.meta?.includeProvisionalPostLinesAfterTail && netProvisionalCosts.length > 0) {
            // Add grand total line
            result.push({
                type: ViewModelLineType.TOTAL,
                index: index++,
                aggregation: simpleGlobalAggregation(),
                section: 'tail',
            });

            // Add subcategory header for net provisional costs
            result.push({
                type: ViewModelLineType.SUBCATEGORY,
                index: index++,
                aggregation: createEmptyAggregation(),
                description: renderLanguage === Language.EN ? 'Provisional costs' : 'Stelposten',
                code: '',
                section: 'tail'
            });

            for (const prov of netProvisionalCosts) {
                // Create aggregation with inversed costs
                const runningAggregation = Object.assign(
                    createEmptyAggregation(),
                    {
                        all: {
                            gross: prov.aggregation.all.gross,
                            net: prov.aggregation.all.net,
                            norm: 0,
                            unitPrice: 0

                        }
                    } satisfies Partial<Aggregation>
                );

                result.push({
                    type: ViewModelLineType.READ_ONLY,
                    index: index++,
                    description: prov.line.description,
                    aggregation: runningAggregation,
                    line: prov.line,
                    section: 'tail'
                });

                mergeAggregatedValues(calcAggregationExclVat, runningAggregation)
            }
        }

        // Add grand total line
        result.push({
            type: ViewModelLineType.TOTAL,
            index: index++,
            aggregation: simpleGlobalAggregation(),
            description: renderLanguage === Language.EN ? 'Total calculation' : 'Totaal calculatie',
            section: 'tail'
        })
    }

    // Only use gross total for the grand total
    const grandTotal = {
        ...createEmptyAggregation(),
        all: {
            gross: calcAggregationExclVat.all.gross,
            net: undefined, // No net total for grand total
            norm: undefined, // No norm total for grand total
            unitPrice: undefined // No unit price for grand total
        }
    }

    // Set aggregation including VAT
    calcAggregationInclVat = mergeAggregatedValues(createEmptyAggregation(), calcAggregationExclVat);

    // Grand total
    result.push({
        
        type: ViewModelLineType.CATEGORY,
        index: index++,
        aggregation: grandTotal,
        description: renderLanguage === Language.EN ? 'TOTAL EXL. V.A.T.' : 'TOTAAL EXCLUSIEF B.T.W.',
        code: '',
        section: 'calculation'
    });

    // Add VAT totals
    for (const [vatType, vatTotal] of Object.entries(vatOverAmount).filter(([_, value]) => (value.all?.gross ?? 0) > 0)) {
        // console.log(vatType, vatTotal);
        // Create line aggregation for VAT line
        const vatLineAggregation: Aggregation = {
            ...createEmptyAggregation(),
            all: {
                gross: (vatTotal.all.gross ?? 0) * VatRates[vatType as VatType].percentage,
                net: 0,
                norm: 0,
                unitPrice: 0
            }
        };

        result.push({
            type: ViewModelLineType.READ_ONLY,
            index: index++,
            aggregation: vatLineAggregation,
            description: `${VatRates[vatType as VatType].label[renderLanguage ?? Language.NL]} ${formatterCurrency.format(vatTotal.all.gross)}`,
            code: '',
            section: 'tail',
            hidden: true, // Hide this line in the UI, but keep it for aggregation
        });

        mergeAggregatedValues(calcAggregationInclVat, vatLineAggregation);
    }

    result.push({
        type: ViewModelLineType.CATEGORY,
        index: index++,
        aggregation: {
            ...createEmptyAggregation(),
            all: {
                gross: calcAggregationInclVat.all.gross,
                net: undefined,
                norm: undefined,
                unitPrice: undefined,
            }
        },
        description: renderLanguage === Language.EN ? 'TOTAL INCL. V.A.T.' : 'TOTAAL INCLUSIEF B.T.W.',
        code: '',
        section: 'tail',
        hidden: true // Hide this line in the UI, but keep it for aggregation
    });


    // Now update selection
    let newSelection: CalculationSelection;

    if (selectLineId) {
        // Get the selected view line
        const selectedViewLine = result.find(l => l.line?.id === selectLineId);
        // const previouslySelectedColumn = state.currentSelection?.current.column ?? 0;

        if (selectedViewLine) {
            newSelection = {
                current: {
                    // Always select column with index 1
                    column: 1, // getClosestColumn(state.columns[selectedViewLine.type], previouslySelectedColumn)?.index,
                    row: selectedViewLine.index
                },
                selectedLines: [selectedViewLine.line]
            }
        } else {
            // console.log(`Line with id ${selectLineId} not found`);
        }
    } else if (state.currentSelection && !!state.currentSelection.selectedLines?.length) {
        const newSelectedLines = state.currentSelection.selectedLines?.map(l => result.find(fnd => fnd.line?.id === l.id)).filter(Boolean);

        if (!!newSelectedLines.length) {
            // console.log(newSelectedLines);
            newSelection = {
                current: {
                    column: state.currentSelection.current?.column ?? 1,
                    row: newSelectedLines[0].index
                },
                selectedLines: newSelectedLines.map(l => l.line)
            }
        }
    }

    // console.log('VAT', vatOverAmount);

    console.log('RENDER TIME', new Date().valueOf() - t0);

    return {
        ...state,
        viewModelLines: result,
        aggregation: calcAggregationExclVat,
        warnings: warnings,
        currentSelection: newSelection,
        lines: sortedLines,
        tail: sortedTailLines
    }
}

function addOverheadAndProfitToVatTotals(costs: number, vatTotals: Record<VatType, Aggregation>, vatRatios: Record<VatType, number>) {
    // console.log(vatRatios);
    const vatRatesUsed = Object.entries(vatRatios).filter(([_, value]) => value > 0);

    for (const [vatRate] of vatRatesUsed) {
        const ratio = vatRatios[vatRate as VatType];
        const overheadAndProfit = costs * ratio;

        // Add overhead and profit to VAT totals
        vatTotals[vatRate].all.gross += overheadAndProfit;
    }
}

function deepCopy<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (Array.isArray(obj)) {
        const arrCopy = [] as any[];
        for (const item of obj) {
            arrCopy.push(deepCopy(item));
        }
        return arrCopy as any;
    }

    const objCopy = {} as { [key: string]: any };
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            objCopy[key] = deepCopy((obj as { [key: string]: any })[key]);
        }
    }
    return objCopy as T;
}

function getLineWarnings(line: CalculationLineApi) {
    // Warnings are only for calculation lines
    if (line.isTextLine) {
        return undefined;
    }

    const result: WarningType[] = [
        line.quantity === '' || line.quantity === undefined || line.quantity === null ? WarningType["MISSING QUANTITY"] : undefined,
        line.description === '' || line.description === undefined || line.description === null ? WarningType["MISSING DESCRIPTION"] : undefined,
        line.hours?.norm === undefined && line.material?.norm === undefined && line.equipment?.norm === undefined && line.subcontracting.norm === undefined ? WarningType["MISSING NORM"] : undefined
    ].filter(f => f !== undefined);

    return !!result.length ? result : undefined;
}

function isEmptyAggregation(a: Aggregation) {
    return !(a.all.net || a.equipment.net || a.hours.net || a.material.net || a.subcontracting.net);
}

const formatterCurrency = new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
})